import React from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    IconButton, 
    Typography, 
    Box, 
    Grid 
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { StyledTextInput, StyledButton, StyledButtonOutlined } from '../../../../theme/Styles';
import COLORS from '../../../../theme/Colors';

const ContactFormModal = ({ open, onClose, serviceName }) => {
    const initialValues = {
        name: '',
        email: '',
        phone: '',
        message: ''
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().required('Este campo es obligatorio'),
        email: Yup.string().email('Email inválido').required('Este campo es obligatorio'),
        phone: Yup.string().required('Este campo es obligatorio'),
        message: Yup.string()
    });

    const handleSubmit = (values) => {
        // En un entorno real aquí se llamaría a la API
        console.log("Datos del formulario de contacto:", values);
        onClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="sm" 
            fullWidth
            PaperProps={{
                sx: { borderRadius: '12px', p: 1 }
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: COLORS.secondary, lineHeight: 1.2 }}>
                        Solicitar más información
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                        Completa el formulario para recibir más información sobre: <strong>{serviceName}</strong>
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'text.secondary', p: 0.5 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, color: 'text.secondary' }}>
                    <InfoOutlinedIcon sx={{ fontSize: 18, mr: 1 }} />
                    <Typography variant="body2">
                        Los campos marcados con <span style={{ color: COLORS.primary }}>*</span> son obligatorios.
                    </Typography>
                </Box>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, errors, touched, handleChange, handleBlur }) => (
                        <Form>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: COLORS.secondary }}>
                                        Nombre completo <span style={{ color: COLORS.primary }}>*</span>
                                    </Typography>
                                    <StyledTextInput
                                        name="name"
                                        placeholder="Tu nombre"
                                        fullWidth
                                        value={values.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.name && Boolean(errors.name)}
                                        helperText={touched.name && errors.name}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: COLORS.secondary }}>
                                        Correo electrónico <span style={{ color: COLORS.primary }}>*</span>
                                    </Typography>
                                    <StyledTextInput
                                        name="email"
                                        placeholder="tu@email.com"
                                        fullWidth
                                        value={values.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.email && Boolean(errors.email)}
                                        helperText={touched.email && errors.email}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: COLORS.secondary }}>
                                        Teléfono <span style={{ color: COLORS.primary }}>*</span>
                                    </Typography>
                                    <StyledTextInput
                                        name="phone"
                                        placeholder="(809) 000-0000"
                                        fullWidth
                                        value={values.phone}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.phone && Boolean(errors.phone)}
                                        helperText={touched.phone && errors.phone}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: COLORS.secondary }}>
                                        Mensaje
                                    </Typography>
                                    <StyledTextInput
                                        name="message"
                                        placeholder="¿Qué información adicional necesitas?"
                                        fullWidth
                                        multiline
                                        rows={4}
                                        value={values.message}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                </Grid>
                            </Grid>

                            <DialogActions sx={{ px: 0, py: 3, mt: 2, display: 'flex', gap: 2 }}>
                                <StyledButtonOutlined 
                                    type="button"
                                    onClick={onClose} 
                                    sx={{ 
                                        width: 'auto', 
                                        minWidth: '120px',
                                        px: 4, 
                                        borderRadius: '4px', 
                                        borderColor: '#e0e0e0', 
                                        color: COLORS.black,
                                        '&:hover': { borderColor: COLORS.secondary, bgcolor: 'transparent', color: COLORS.secondary }
                                    }}
                                >
                                    Cancelar
                                </StyledButtonOutlined>
                                <StyledButton 
                                    type="submit" 
                                    sx={{ 
                                        width: 'auto', 
                                        minWidth: '160px',
                                        px: 4, 
                                        borderRadius: '4px', 
                                        bgcolor: COLORS.secondary,
                                        '&:hover': { bgcolor: COLORS.primary }
                                    }}
                                >
                                    Enviar solicitud
                                </StyledButton>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    );
};

export default ContactFormModal;
